import { makeAutoObservable } from 'mobx';
import { trackAssetsFlatViewToggle } from 'src/services/tracker/trackers/assets';
import AuthenticationStore from './AuthenticationStore';
import { RootStoreSerialization } from './types';
import FilterStore, { FilterState } from './FilterStore';
import NavigationPanelStore from './NavigationPanelStore';
import OverviewPanelStore from './OverviewPanelStore';

export type PersistableRootStore = {
	serializationToPersist: () => object;
	rehydrateWithSerialization: ( serialization: object ) => void;
};

type RootStoreParams = {
	isAuthenticated?: boolean;
	filterState?: FilterState;
};

export class RootStore implements PersistableRootStore {
	authenticationStore: AuthenticationStore;
	filterStore: FilterStore;
	navigationPanelStore: NavigationPanelStore;
	overviewPanelStore: OverviewPanelStore;
	assetsFlatViewEnabled = false;

	constructor( { isAuthenticated, filterState }: RootStoreParams = {} ) {
		this.authenticationStore = new AuthenticationStore( isAuthenticated );
		this.filterStore = new FilterStore( filterState );
		this.navigationPanelStore = new NavigationPanelStore();
		this.overviewPanelStore = new OverviewPanelStore();

		makeAutoObservable( this );
	}

	get isAuthenticated() {
		return this.authenticationStore.isAuthenticated;
	}

	toggleAssetsFlatView = () => {
		this.assetsFlatViewEnabled = !this.assetsFlatViewEnabled;
		trackAssetsFlatViewToggle( { enabled: this.assetsFlatViewEnabled } );
	}

	serializationToPersist(): RootStoreSerialization {
		return {
			authenticationStore: this.authenticationStore.serialize(),
			filterStore: this.filterStore.serialize(),
			navigationPanelStore: this.navigationPanelStore.serialize(),
			overviewPanelStore: this.overviewPanelStore.serialize()
		};
	}

	rehydrateWithSerialization( {
		authenticationStore, filterStore, navigationPanelStore, overviewPanelStore
	}: RootStoreSerialization ) {
		const storeEntries = [
			[ this.authenticationStore, authenticationStore ],
			[ this.filterStore, filterStore ],
			[ this.navigationPanelStore, navigationPanelStore ],
			[ this.overviewPanelStore, overviewPanelStore ]
		] as const;

		storeEntries.forEach( ( [ store, serialization ] ) => {
			if ( serialization ) {
				( store as { hydrate: ( s: unknown ) => void } ).hydrate( serialization as unknown );
			}
		} );
	}

	clearStore() {
		const stores = [
			this.authenticationStore,
			this.filterStore,
			this.navigationPanelStore,
			this.overviewPanelStore
		];

		stores.forEach( store => store.clear() );
		this.assetsFlatViewEnabled = false;
	}
}
